import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowLeft, MailCheck } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background grain p-6">
      <div className="w-full max-w-sm">
        <div className="mb-8"><Logo size={40} /></div>
        {sent ? (
          <div data-testid="forgot-success">
            <MailCheck className="h-8 w-8 text-[#E1FF00] mb-4" />
            <h2 className="font-display text-2xl text-white mb-2">Check your inbox</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              If that email is registered, a reset link has been sent. The link expires in one hour.
            </p>
            <Link to="/login" className="inline-flex items-center gap-2 text-sm text-white mt-6 hover:text-[#E1FF00] transition-colors" data-testid="back-to-login-link">
              <ArrowLeft className="h-4 w-4" /> Back to sign in
            </Link>
          </div>
        ) : (
          <>
            <p className="overline mb-2">Password reset</p>
            <h2 className="font-display text-3xl text-white mb-6">Reset your password</h2>
            <form onSubmit={submit} className="space-y-5" data-testid="forgot-form">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  required data-testid="forgot-email-input" className="bg-secondary border-border h-11" />
              </div>
              {error && <div className="text-sm text-[#FF6666]" data-testid="forgot-error">{error}</div>}
              <Button type="submit" disabled={loading} className="w-full h-11 bg-white text-black hover:bg-white/90 font-semibold" data-testid="forgot-submit-button">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send reset link"}
              </Button>
            </form>
            <Link to="/login" className="inline-flex items-center gap-2 text-sm text-muted-foreground mt-6 hover:text-white transition-colors" data-testid="back-to-login-link">
              <ArrowLeft className="h-4 w-4" /> Back to sign in
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
